<template>
    <div class="content">
        <base-header class="pb-6">
            <div class="row align-items-center py-4">
                <div class="col-lg-6 col-7">
                    <h6 class="h2 text-white d-inline-block mb-0">
                        {{ isNew ? $t("app_catalog.btn_add_product") : $t("app_catalog.edit_product_title", { id: $route.params.id }) }}
                    </h6>
                    <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
                        <route-bread-crumb></route-bread-crumb>
                    </nav>
                </div>
                <div class="col-lg-6 col-5 text-right">
                    <!-- Form draft spec 2026-10-07 §3 F2 / §4 A8: create mode
                         only, leftmost of the header group. -->
                    <form-clear-button v-if="isNew" :disabled="saving" @click="clearForm"></form-clear-button>
                    <!-- #263: push this seller product to a marketplace. Hidden
                         on a product that does not exist yet — there is nothing
                         to push, and the endpoint takes a real product id. -->
                    <base-button
                        v-if="!isNew && isAdmin"
                        size="md"
                        type="neutral"
                        @click="openPushModal"
                    >
                        <i class="fa fa-cloud-upload-alt"></i> {{ $t("amaze_push.btn_open") }}
                    </base-button>
                    <base-button size="md" type="secondary" @click="$router.back()">
                        <i class="fa fa-arrow-left"></i> {{ $t("back") }}
                    </base-button>
                </div>
            </div>
        </base-header>

        <amaze-push-modal
            :show.sync="pushModalOpen"
            :product-id="$route.params.id"
            :product-name="form.name_th || form.name_en || form.code"
        ></amaze-push-modal>

        <div class="container-fluid mt--6">
            <div v-loading="loadingProduct">
                <card>
                    <h4 slot="header" class="mb-0">{{ $t("app_catalog.section_basic_info") }}</h4>

                    <el-form
                        ref="productForm"
                        :model="form"
                        :rules="rules"
                        label-width="160px"
                    >
                        <el-form-item :label="$t('app_catalog.col_name_th')" prop="name_th">
                            <el-input v-model="form.name_th" style="max-width: 420px;"></el-input>
                        </el-form-item>
                        <el-form-item :label="$t('app_catalog.col_name_en')" prop="name_en">
                            <el-input v-model="form.name_en" style="max-width: 420px;"></el-input>
                        </el-form-item>
                        <el-form-item :label="$t('app_catalog.col_code')" prop="code">
                            <el-input v-model="form.code" style="max-width: 420px;"></el-input>
                        </el-form-item>
                        <el-form-item :label="$t('app_catalog.col_detail')">
                            <vue-editor
                                v-model="form.detail"
                                :editor-toolbar="detailToolbar"
                                use-custom-image-handler
                                @image-added="handleDetailImageAdded"
                            ></vue-editor>
                        </el-form-item>
                        <el-form-item :label="$t('app_catalog.col_category')">
                            <el-select
                                v-model="form.bo_product_category_id"
                                :placeholder="$t('app_catalog.select_category')"
                                filterable
                                class="w-100"
                            >
                                <el-option
                                    v-for="cat in categoryList"
                                    :key="cat.id"
                                    :label="cat.name_th || cat.name"
                                    :value="cat.id"
                                ></el-option>
                            </el-select>
                        </el-form-item>
                        <el-form-item :label="$t('app_catalog.col_price')" prop="price">
                            <el-input-number v-model="form.price" :min="0" :precision="2" :controls="false" style="width: 240px;"></el-input-number>
                            <div v-if="form.price_mode === 'fixed'" class="text-muted small mt-1">
                                {{ $t("app_catalog.product_fixed_price_hint") }}
                            </div>
                        </el-form-item>
                        <el-form-item :label="$t('app_catalog.product_price_mode')">
                            <el-radio-group v-model="form.price_mode">
                                <el-radio label="per_sku">{{ $t("app_catalog.product_price_per_sku") }}</el-radio>
                                <el-radio label="fixed">{{ $t("app_catalog.product_price_fixed") }}</el-radio>
                            </el-radio-group>
                        </el-form-item>
                        <el-form-item :label="$t('app_catalog.col_cod')">
                            <el-switch v-model="form.cod" :active-value="1" :inactive-value="0"></el-switch>
                        </el-form-item>
                        <!-- Sale status (design 2026-10-03 §5). Same field, same
                             payload — `form.app_enable` — read in plain words:
                             the switch says เปิดการขาย / ปิดการขาย instead of
                             "เปิดใน App", because the storefront is the web shop
                             as well as the app. -->
                        <el-form-item :label="$t('app_catalog.col_app_enable')">
                            <!-- One state word, not both: el-switch's active/inactive
                                 texts render on either side at once and read as a
                                 choice between two labels. The owner asked for the
                                 CURRENT state only (2026-10-03). -->
                            <el-switch v-model="form.app_enable" :active-value="1" :inactive-value="0"></el-switch>
                            <span
                                class="ml-2 font-weight-bold"
                                :class="form.app_enable === 1 ? 'text-success' : 'text-muted'"
                                data-test="sale-status-word"
                            >{{ form.app_enable === 1 ? $t("app_catalog.sale_on") : $t("app_catalog.sale_off") }}</span>
                            <div class="mt-1">
                                <small class="text-muted">{{ $t("app_catalog.sale_enable_hint") }}</small>
                            </div>
                        </el-form-item>
                        <el-form-item :label="$t('app_catalog.col_app_tag')">
                            <el-input v-model="form.app_tag" style="max-width: 420px;"></el-input>
                        </el-form-item>
                        <!-- `visibility` is the Backoffice-side audience flag
                             (0 ทุกคน / 1 เฉพาะบริษัท / 2 เฉพาะตัวแทน — controllers/jwt.go
                             ProductVisibility). The storefront never reads it
                             (member reads filter on app_enable + status only), and
                             a 0/1 switch mislabelled it as "แสดงผล". Removed from this
                             form on 2026-10-03; the value is still carried in the
                             payload unchanged so the full-replace PUT keeps it. -->
                    </el-form>
                </card>

                <card class="mt-3">
                    <h4 slot="header" class="mb-0">{{ $t("app_catalog.section_images") }}</h4>
                    <s3-multi-image-upload
                        object-type="product"
                        v-model="form.images"
                        :max="10"
                        large
                    ></s3-multi-image-upload>
                </card>

                <!-- Option Groups section (above SKU table) -->
                <card class="mt-3">
                    <div slot="header" class="d-flex justify-content-between align-items-center">
                        <h4 class="mb-0">{{ $t("app_catalog.section_option_groups") }}</h4>
                        <base-button size="sm" type="info" @click="addOptionGroup">
                            <i class="fa fa-plus"></i> {{ $t("app_catalog.add_option_group") }}
                        </base-button>
                    </div>

                    <div v-if="form.option_groups.length === 0" class="text-muted small mb-2">
                        {{ $t("app_catalog.no_data") }}
                    </div>

                    <div
                        v-for="(group, gIdx) in form.option_groups"
                        :key="gIdx"
                        class="border rounded p-3 mb-3"
                    >
                        <div class="d-flex align-items-center mb-2" style="gap: 8px;">
                            <el-input
                                v-model="group.label_th"
                                size="small"
                                :placeholder="$t('app_catalog.option_group_placeholder')"
                                style="width: 200px;"
                            ></el-input>
                            <el-input
                                v-model="group.label_en"
                                size="small"
                                placeholder="Group name (EN)"
                                style="width: 200px;"
                            ></el-input>
                            <base-button size="sm" type="danger" @click="removeOptionGroup(gIdx)">
                                <i class="fa fa-times"></i>
                            </base-button>
                        </div>
                        <div class="mb-1 small text-muted">{{ $t("app_catalog.option_group_values") }}</div>
                        <div class="d-flex flex-wrap align-items-center" style="gap: 6px;">
                            <el-tag
                                v-for="(val, vIdx) in group.values"
                                :key="vIdx"
                                closable
                                size="small"
                                @close="removeOptionValue(gIdx, vIdx)"
                            >{{ val.label_th || val.label_en }}</el-tag>
                            <div v-if="addingValueFor === gIdx" class="d-flex align-items-center" style="gap: 4px;">
                                <el-input
                                    ref="newValueInput"
                                    v-model="newValueText"
                                    size="mini"
                                    style="width: 140px;"
                                    :placeholder="$t('app_catalog.option_value_placeholder')"
                                    @keyup.enter.native="confirmAddValue(gIdx)"
                                    @blur="confirmAddValue(gIdx)"
                                ></el-input>
                            </div>
                            <base-button
                                v-else
                                size="sm"
                                type="secondary"
                                style="padding: 2px 8px;"
                                @click="startAddValue(gIdx)"
                            >
                                <i class="fa fa-plus"></i>
                            </base-button>
                        </div>
                    </div>

                    <div v-if="form.option_groups.length > 0" class="mt-3">
                        <base-button
                            type="primary"
                            size="sm"
                            :disabled="!canGenerateMatrix"
                            @click="tryGenerateSkuMatrix"
                        >
                            <i class="fa fa-th"></i> {{ $t("app_catalog.generate_sku_matrix") }}
                        </base-button>
                    </div>
                </card>

                <card class="mt-3">
                    <h4 slot="header" class="mb-0">{{ $t("app_catalog.section_skus") }}</h4>

                    <!-- #257: the flash-sale columns are off unless this product already
                         uses them (see showFlashColumns). They only ever applied to a
                         handful of SKUs and pushed the columns that matter off screen. -->
                    <div class="mb-2">
                        <el-checkbox v-model="showFlashColumns">{{ $t("app_catalog.sku_show_flash") }}</el-checkbox>
                    </div>

                    <el-table :data="form.skus" row-key="_idx" size="small">
                        <el-table-column :label="$t('app_catalog.sku_name')" min-width="180px">
                            <template slot-scope="scope">
                                <!-- #257: free text again. The name is the operator's own
                                     label ("ดำ / แดง"); the generated WMS string lives in
                                     sku_code, the next column. -->
                                <el-input
                                    v-model="scope.row.sku_name"
                                    size="small"
                                    :class="{ 'sku-name-missing': skuNameMissing(scope.row) }"
                                ></el-input>
                                <!-- Owner rule 2026-10-15: a SKU with no option name is
                                     incomplete and may not be put on sale. Marked only
                                     after a save was refused (client guard or the BE's
                                     400 sku_name_required), and clears as soon as a
                                     name is typed. -->
                                <div
                                    v-if="skuNameMissing(scope.row)"
                                    class="text-danger small mt-1"
                                    data-testid="sku-name-missing"
                                >{{ $t("app_catalog.sku_name_required") }}</div>
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('app_catalog.sku_code')" min-width="180px">
                            <template slot-scope="scope">
                                <!-- #246 + #257: the SKU CODE is GENERATED from the mapped
                                     WMS products (codes joined with "+"), never typed — the
                                     App stock API and the platform auto-mapper both key
                                     combos by this string. There is deliberately no
                                     v-model / input here: the only way to change it is the
                                     dialog behind this edit button. -->
                                <div class="d-flex align-items-center">
                                    <span class="sku-code-readonly flex-grow-1">{{ scope.row.sku_code }}</span>
                                    <base-button
                                        size="sm"
                                        type="secondary"
                                        style="padding: 2px 6px;"
                                        :title="$t('app_catalog.sku_wms_edit')"
                                        @click="openSkuWmsDialog(scope.$index)"
                                    >
                                        <i class="fa fa-edit"></i>
                                    </base-button>
                                </div>
                            </template>
                        </el-table-column>
                        <el-table-column v-if="form.price_mode !== 'fixed'" :label="$t('app_catalog.sku_price')" min-width="120px">
                            <template slot-scope="scope">
                                <el-input-number
                                    v-model="scope.row.price"
                                    :min="0"
                                    :precision="2"
                                    :controls="false"
                                    size="small"
                                    style="width:100%"
                                ></el-input-number>
                            </template>
                        </el-table-column>
                        <!-- #301 (shared stock §8): a SKU whose wms_map resolves
                             completely has its quantity OWNED BY THE WAREHOUSE —
                             services/stockshared recomputes and overwrites it every
                             20 s. Leaving an input here would let staff type a
                             number that is pushed over within the minute, which is
                             why §9 step 4 requires this screen before
                             shared_mode: write. The gate is `stock_source` from the
                             API, NOT wms_map: a HALF-mapped SKU (#583) is still
                             manual and must stay editable. A SKU with no
                             stock_source at all (older API / shared_enabled: false)
                             behaves exactly as before. -->
                        <el-table-column :label="$t('app_catalog.sku_quantity')" min-width="150px">
                            <template slot-scope="scope">
                                <div v-if="isWmsQuantity(scope.row)" :title="wmsQuantityHint(scope.row)">
                                    <div class="d-flex align-items-center">
                                        <span class="wms-quantity-readonly mr-1">{{ scope.row.quantity }}</span>
                                        <el-tag type="info" size="mini">
                                            {{ $t("app_catalog.sku_quantity_from_wms") }}
                                        </el-tag>
                                    </div>
                                    <div class="text-muted small mt-1">{{ wmsQuantityHint(scope.row) }}</div>
                                </div>
                                <div v-else>
                                    <el-input-number
                                        v-model="scope.row.quantity"
                                        :min="0"
                                        :precision="0"
                                        :controls="false"
                                        size="small"
                                        style="width:100%"
                                    ></el-input-number>
                                    <!-- Only when the API explicitly said "manual" —
                                         not on every row of a build talking to an
                                         API that knows nothing about stock_source. -->
                                    <div v-if="isManualQuantity(scope.row)" class="text-muted small mt-1">
                                        {{ $t("app_catalog.sku_quantity_manual_hint") }}
                                    </div>
                                </div>
                            </template>
                        </el-table-column>
                        <!-- B7 §6/§7.7: a SKU whose flash window is OWNED BY THE
                             SHOPEE MIRROR (`flash_source = 'shopee'`) is read-only
                             here. services/shopeepromo rewrites those three fields
                             every ticker round (10 min), so an input would let staff
                             type a price that is pushed over without a word — which
                             is why §9 step 5 requires this screen before
                             `mode: write`. The gate is `flash_source`, never "has a
                             promotion link": a link whose promotion has ended leaves
                             the window free again. -->
                        <el-table-column v-if="showFlashColumns" :label="$t('app_catalog.sku_flash_price')" min-width="130px">
                            <template slot-scope="scope">
                                <div v-if="isShopeeFlash(scope.row)">
                                    <el-tooltip
                                        :content="$t('shopee_promotion.flash_from_shopee_hint')"
                                        placement="top"
                                    >
                                        <div class="d-flex align-items-center">
                                            <span class="shopee-flash-readonly mr-1">
                                                {{ flashPriceText(scope.row) }}
                                            </span>
                                            <el-tag type="info" size="mini">
                                                {{ $t("shopee_promotion.flash_from_shopee") }}
                                            </el-tag>
                                        </div>
                                    </el-tooltip>
                                </div>
                                <el-input-number
                                    v-else
                                    v-model="scope.row.flash_price"
                                    :min="0"
                                    :precision="2"
                                    :controls="false"
                                    size="small"
                                    style="width:100%"
                                    :placeholder="$t('app_catalog.sku_flash_price')"
                                ></el-input-number>
                            </template>
                        </el-table-column>
                        <el-table-column v-if="showFlashColumns" :label="$t('app_catalog.sku_flash_window')" min-width="300px">
                            <template slot-scope="scope">
                                <div v-if="isShopeeFlash(scope.row)" :title="$t('shopee_promotion.flash_from_shopee_hint')">
                                    <span class="shopee-flash-readonly">{{ flashWindowText(scope.row) }}</span>
                                    <span class="d-block text-muted small">
                                        {{ $t("shopee_promotion.flash_from_shopee_hint") }}
                                    </span>
                                </div>
                                <el-date-picker
                                    v-else
                                    v-model="scope.row.flashRange"
                                    type="datetimerange"
                                    range-separator="-"
                                    :start-placeholder="$t('app_catalog.col_starts_at')"
                                    :end-placeholder="$t('app_catalog.col_ends_at')"
                                    value-format="yyyy-MM-dd HH:mm:ss"
                                    size="small"
                                    style="width: 280px; max-width: 100%;"
                                ></el-date-picker>
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('app_catalog.sku_bonus_points')" min-width="110px">
                            <template slot-scope="scope">
                                <el-input-number
                                    v-model="scope.row.bonus_points"
                                    :min="0"
                                    :step="1"
                                    step-strictly
                                    size="mini"
                                    controls-position="right"
                                    style="width: 100%"
                                ></el-input-number>
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('app_catalog.sku_images')" min-width="100px" align="center">
                            <template slot-scope="scope">
                                <div class="d-flex flex-column align-items-center">
                                    <img
                                        v-if="skuImageUrl(scope.row)"
                                        :src="skuImageUrl(scope.row)"
                                        style="width: 36px; height: 36px; object-fit: cover;"
                                        class="rounded mb-1"
                                        @error="$event.target.style.display = 'none'"
                                    />
                                    <base-button
                                        size="sm"
                                        type="secondary"
                                        style="padding: 2px 6px;"
                                        @click="triggerSkuImage(scope.$index)"
                                    >
                                        <i class="fa fa-upload"></i>
                                        {{ skuImageUrl(scope.row) ? $t("app_catalog.option_change_image") : $t("app_catalog.option_upload_image") }}
                                    </base-button>
                                </div>
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('actions')" min-width="70px" align="center">
                            <template slot-scope="scope">
                                <base-button size="sm" type="danger" @click="removeSku(scope.$index)">
                                    <i class="fa fa-times"></i>
                                </base-button>
                            </template>
                        </el-table-column>
                    </el-table>

                    <base-button size="sm" type="info" class="mt-2" @click="addSku">
                        <i class="fa fa-plus"></i> {{ $t("app_catalog.add_sku") }}
                    </base-button>

                    <!-- Hidden file input for per-SKU single-image upload -->
                    <input
                        ref="skuImageInput"
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        style="display: none;"
                        @change="onSkuImageChange"
                    />
                </card>

                <card class="mt-3">
                    <h4 slot="header" class="mb-0">{{ $t("app_catalog.section_discount") }}</h4>
                    <el-form label-width="160px">
                        <el-form-item :label="$t('app_catalog.col_app_discount')">
                            <el-input-number v-model="form.app_discount" :min="0" :precision="2" :controls="false"></el-input-number>
                        </el-form-item>
                        <el-form-item :label="$t('app_catalog.col_discount_start')">
                            <el-date-picker
                                v-model="form.app_discount_start"
                                type="datetime"
                                value-format="yyyy-MM-dd HH:mm:ss"
                                style="width: 240px;"
                            ></el-date-picker>
                        </el-form-item>
                        <el-form-item :label="$t('app_catalog.col_discount_end')">
                            <el-date-picker
                                v-model="form.app_discount_end"
                                type="datetime"
                                value-format="yyyy-MM-dd HH:mm:ss"
                                style="width: 240px;"
                            ></el-date-picker>
                        </el-form-item>
                    </el-form>
                </card>

                <div class="text-right mt-3 mb-4">
                    <base-button type="secondary" class="mr-2" @click="$router.back()">{{ $t("cancel") }}</base-button>
                    <base-button type="primary" :loading="saving" @click="submitForm">{{ $t("save") }}</base-button>
                </div>
            </div>
        </div>

        <!-- Matrix confirm dialog -->
        <el-dialog
            :visible.sync="matrixConfirmVisible"
            width="480px"
            :title="$t('app_catalog.generate_sku_matrix')"
        >
            <div>{{ $t("app_catalog.matrix_regenerate_confirm") }}</div>
            <div v-if="matrixDropList.length > 0" class="mt-2">
                <div class="small text-danger">
                    <span v-if="matrixDropList.length <= 10">
                        {{ matrixDropList.join(", ") }}
                    </span>
                    <span v-else>
                        {{ matrixDropList.slice(0, 10).join(", ") }} ... (+{{ matrixDropList.length - 10 }})
                    </span>
                </div>
            </div>
            <span slot="footer" class="dialog-footer">
                <base-button type="secondary" @click="matrixConfirmVisible = false">{{ $t("cancel") }}</base-button>
                <base-button type="primary" @click="doGenerateSkuMatrix">{{ $t("save") }}</base-button>
            </span>
        </el-dialog>

        <!-- SKU name <- WMS product mapping dialog (#246) -->
        <el-dialog
            :visible.sync="skuWmsDialogVisible"
            width="620px"
            :title="$t('app_catalog.sku_wms_modal_title')"
            append-to-body
        >
            <div class="row">
                <div class="col-md-9">
                    <el-select
                        v-model="skuWmsPickId"
                        filterable
                        clearable
                        size="small"
                        style="width: 100%"
                        :loading="skuWmsLoading"
                        :filter-method="onSkuWmsQuery"
                        :placeholder="$t('app_catalog.sku_wms_select_placeholder')"
                    >
                        <el-option
                            v-for="opt in skuWmsOptions"
                            :key="wmsRowId(opt)"
                            :label="wmsRowLabel(opt)"
                            :value="wmsRowId(opt)"
                        ></el-option>
                    </el-select>
                </div>
                <div class="col-md-3 text-right">
                    <base-button
                        size="sm"
                        type="primary"
                        :disabled="!skuWmsPickId"
                        @click="addSkuWmsItem"
                    >
                        <i class="fa fa-plus"></i> {{ $t("app_catalog.sku_wms_add") }}
                    </base-button>
                </div>
            </div>

            <div class="mt-3">
                <div v-if="skuWmsDraft.length === 0" class="text-muted small">
                    {{ $t("app_catalog.sku_wms_empty") }}
                </div>
                <!-- #251: the loop runs over the DRAFT ARRAY, not over a computed
                     projection of it, so the quantity the user types is written
                     back into the object confirm will save. -->
                <div
                    v-for="(item, iIdx) in skuWmsDraft"
                    :key="item.wms_product_id"
                    class="d-flex align-items-center border-bottom py-2"
                >
                    <span class="mr-2 font-weight-bold">{{ skuWmsRowCode(item) }}</span>
                    <span class="flex-grow-1 small text-muted">{{ skuWmsRowName(item) }}</span>
                    <span class="mr-2 small text-muted">{{ $t("app_catalog.sku_wms_qty") }}</span>
                    <el-input-number
                        v-model="item.pickup_quantity"
                        :min="1"
                        :max="skuWmsQtyMax"
                        :step="1"
                        :precision="0"
                        size="mini"
                        class="mr-2"
                        style="width: 110px;"
                        :title="$t('app_catalog.sku_wms_qty_hint')"
                    ></el-input-number>
                    <base-button
                        size="sm"
                        type="danger"
                        style="padding: 2px 6px;"
                        :title="$t('app_catalog.sku_wms_remove')"
                        @click="removeSkuWmsItem(iIdx)"
                    >
                        <i class="fa fa-trash"></i>
                    </base-button>
                </div>
            </div>

            <div class="mt-3 small">
                {{ $t("app_catalog.sku_wms_preview") }}:
                <strong>{{ skuWmsPreviewCode }}</strong>
                <!-- #257: mdfpf_product_sku.sku_code is varchar(100). The length is on
                     screen while the list is being built, and confirm refuses over it. -->
                <span :class="skuWmsPreviewTooLong ? 'text-danger font-weight-bold' : 'text-muted'">
                    ({{ skuWmsPreviewLength }} / {{ skuCodeMaxLength }})
                </span>
            </div>
            <!-- #562: this modal is the only place a SKU is mapped to WMS -->
            <div class="mt-2 small text-muted">
                {{ $t("app_catalog.sku_wms_single_source") }}
            </div>

            <span slot="footer" class="dialog-footer">
                <base-button type="secondary" @click="skuWmsDialogVisible = false">
                    {{ $t("app_catalog.sku_wms_cancel") }}
                </base-button>
                <base-button type="primary" @click="confirmSkuWmsDialog">
                    {{ $t("app_catalog.sku_wms_confirm") }}
                </base-button>
            </span>
        </el-dialog>
    </div>
</template>

<script>
import { apiErrorMessage } from "@/util/api-error";
import { mapGetters, mapActions } from "vuex";
import { requestRefresh } from "@/utils/pageRefresh";
import formDraft from "@/mixins/formDraft";
import FormClearButton from "@/components/FormClearButton.vue";
import {
    Form,
    FormItem,
    Input,
    InputNumber,
    Select,
    Option,
    DatePicker,
    Switch,
    Table,
    TableColumn,
    RadioGroup,
    Radio,
    Tag,
    Dialog,
    Checkbox
} from "element-ui";
import S3MultiImageUpload from "@/components/Inputs/S3MultiImageUpload.vue";
import AmazePushModal from "./components/AmazePushModal.vue";
import { VueEditor } from "vue2-editor";
import { AppCatalogUploadService, WmsService } from "../../resource";
import {
    skuNameFromWmsMap,
    wmsProductCode,
    wmsPickupQuantity,
    normalizeWmsMap,
    WMS_PICKUP_QUANTITY_MAX,
    SKU_CODE_MAX_LENGTH,
    skuCodeLength,
    skuCodeWithinLimit,
    anySkuHasFlashSale,
} from "@/utils/skuName";
// #301 shared stock (spec §8): which SKUs' quantity the warehouse owns, the
// sentence that explains it, and the payload rule. All of it is pure and
// unit-tested in tests/unit/stockSource.spec.js — this component only renders it.
import {
    isWmsSourced,
    isManualSourced,
    wmsHint,
    stripWmsQuantities,
    quantityIgnoredSkus,
} from "@/utils/stockSource";
// B7 §6/§7.7 — who owns a SKU's flash window. `flash_source = 'shopee'` means
// services/shopeepromo rewrites it every ticker round (read-only here), and a
// staff edit of any other SKU must claim the window with 'manual' or the next
// round overwrites it silently. Pure and unit-tested in
// tests/unit/shopeePromotion.spec.js.
import {
    isShopeeFlashSku,
    applyFlashSource,
    formatWindow,
} from "@/utils/shopeePromotion";
// Owner rule 2026-10-15 — a SKU with no option name may not be put on sale.
// Pure and unit-tested in tests/unit/skuIncomplete.spec.js.
import {
    blankNameSkuIndexes,
    optionNameSaveGuard,
    isOptionNameRequiredError,
    SKU_NAME_REQUIRED_KEY,
} from "@/utils/skuIncomplete";

export default {
    name: "AppCatalogProductEdit",
    // Form draft (spec 2026-10-07 §3/§4 A8): create mode keeps its draft while
    // the tab is cached, clears on "ล้างฟอร์ม" and after a successful create.
    mixins: [formDraft],
    components: {
        FormClearButton,
        S3MultiImageUpload,
        // #263 — "sync ไป platform" modal.
        AmazePushModal,
        VueEditor,
        [Form.name]: Form,
        [FormItem.name]: FormItem,
        [Input.name]: Input,
        [InputNumber.name]: InputNumber,
        [Select.name]: Select,
        [Option.name]: Option,
        [DatePicker.name]: DatePicker,
        [Switch.name]: Switch,
        [Table.name]: Table,
        [TableColumn.name]: TableColumn,
        [RadioGroup.name]: RadioGroup,
        [Radio.name]: Radio,
        [Tag.name]: Tag,
        [Dialog.name]: Dialog,
        [Checkbox.name]: Checkbox
    },
    data() {
        return {
            saving: false,
            // Owner rule 2026-10-15 — true once a save was refused because a SKU
            // has no option name; turns on the per-row mark (skuNameMissing).
            skuNameChecked: false,
            loadingProduct: false,
            // #263 — "sync ไป platform" modal visibility.
            pushModalOpen: false,
            // Which SKU index the hidden file input is currently targeting.
            skuImageTarget: null,
            detailToolbar: [
                [{ header: [false, 1, 2, 3] }],
                ["bold", "italic", "underline", "strike"],
                [{ color: [] }, { background: [] }],
                [{ list: "ordered" }, { list: "bullet" }],
                [{ align: [] }],
                ["link", "image"],
                ["clean"]
            ],
            // Option group tag-input state.
            addingValueFor: null,  // gIdx of group currently being typed into
            newValueText: "",
            // Matrix confirm dialog
            matrixConfirmVisible: false,
            matrixDropList: [],    // sku_codes that will be dropped on regen
            // #257: are the flash-sale columns (ราคาแฟลชเซล / ช่วงเวลาแฟลชเซล)
            // rendered? Off for a new product; loadProduct turns it on when the
            // product already has a flash price or window on any SKU. Purely a
            // display flag — hidden columns keep their data and still save.
            showFlashColumns: false,
            // ---- SKU code <- WMS mapping dialog (#246, target field #257) ----
            skuWmsDialogVisible: false,
            skuWmsRowIndex: null,    // which form.skus row the dialog is editing
            skuWmsDraft: [],         // [{ wms_product_id, pickup_quantity }] being edited
            skuWmsPickId: null,      // currently highlighted option in the dropdown
            skuWmsQuery: "",         // dropdown search text (client-side filter)
            skuWmsProducts: [],      // cached GET /api/v3/wms/product rows
            skuWmsLoaded: false,
            skuWmsLoading: false,
            // GET /api/v3/wms/product is not paginated and has no server-side
            // search, so the whole enterprise catalogue arrives in one response.
            // Render at most this many <el-option>s to keep the dropdown usable.
            skuWmsOptionLimit: 100,
            skuWmsQtyMax: WMS_PICKUP_QUANTITY_MAX, // #251: owner-set ceiling per WMS product
            skuCodeMaxLength: SKU_CODE_MAX_LENGTH, // #257: mdfpf_product_sku.sku_code varchar(100)
            // Unsaved option values get a unique NEGATIVE temp id (counts down -1,-2,...)
            // used identically in option_groups[].values[].id and each SKU's
            // option_value_ids, so the BE can map temp ids -> real DB ids on save.
            tmpOptValSeq: 0,
            form: {
                name_th: "",
                name_en: "",
                name: "",
                code: "",
                detail: "",
                bo_product_category_id: null,
                price: 0,
                price_mode: "per_sku",
                cod: 0,
                app_enable: 1,
                app_tag: "",
                visibility: 1,
                images: [],
                option_groups: [],
                skus: [],
                app_discount: 0,
                app_discount_start: null,
                app_discount_end: null
            },
            rules: {
                name_th: [{ required: true, message: this.$t("app_catalog.field_required"), trigger: "blur" }],
                code: [{ required: true, message: this.$t("app_catalog.field_required"), trigger: "blur" }],
                price: [{ required: true, type: "number", message: this.$t("app_catalog.field_required"), trigger: "blur" }]
            }
        };
    },
    computed: {
        isNew() {
            return this.$route.params.id === "new";
        },
        ...mapGetters({
            categoryList: "AppCatalogCategory/list",
            isAdmin: "Auth/isAdmin"
        }),
        // Generate SKU button is enabled only when every group has at least one value.
        canGenerateMatrix() {
            if (this.form.option_groups.length === 0) return false;
            return this.form.option_groups.every(g => g.values && g.values.length > 0);
        },
        // ---- SKU code <- WMS mapping dialog (#246, target field #257) ----
        // Options rendered in the dropdown: client-side filtered + capped. The
        // currently picked option is always kept in the list, otherwise
        // el-select loses its label and shows the raw numeric id.
        skuWmsOptions() {
            const q = (this.skuWmsQuery || "").trim().toLowerCase();
            const all = this.skuWmsProducts;
            const matched = q
                ? all.filter(p => this.wmsRowLabel(p).toLowerCase().indexOf(q) !== -1)
                : all;
            const capped = matched.slice(0, this.skuWmsOptionLimit);
            if (this.skuWmsPickId && !capped.some(p => this.wmsRowId(p) === this.skuWmsPickId)) {
                const picked = all.find(p => this.wmsRowId(p) === this.skuWmsPickId);
                if (picked) return [picked, ...capped];
            }
            return capped;
        },
        // Live preview of the sku_code that pressing confirm will write (#257):
        // codes expanded by their quantity (#251), so typing 2 in a row's
        // quantity box shows "A+A" immediately.
        skuWmsPreviewCode() {
            return skuNameFromWmsMap(this.skuWmsDraft, item =>
                wmsProductCode(this.wmsProductById(item.wms_product_id))
            );
        },
        // #257: the generated code's length, shown next to the preview against
        // the varchar(100) ceiling so the operator sees the wall before hitting it.
        skuWmsPreviewLength() {
            return skuCodeLength(this.skuWmsPreviewCode);
        },
        skuWmsPreviewTooLong() {
            return !skuCodeWithinLimit(this.skuWmsPreviewCode, this.skuCodeMaxLength);
        }
    },
    mounted() {
        this.$store.dispatch("AppCatalogCategory/fetchList", {});
        if (!this.isNew) {
            this.loadProduct();
        }
    },
    methods: {
        // Owner rule 2026-10-15 (blank option name = incomplete, not sellable):
        // the row mark. Same predicate as the save guard — a SKU switched off
        // (is_active === false) is never marked, and (#976) neither is the only
        // switched-on SKU: the rule needs the whole SKU list, not the row.
        skuNameMissing(row) {
            if (!this.skuNameChecked) return false;
            const skus = (this.form && this.form.skus) || [];
            const i = skus.indexOf(row);
            return i >= 0 && blankNameSkuIndexes(skus).includes(i);
        },
        // ---- Form draft (spec 2026-10-07 §3 F1 / §4 A8) ----
        // Back to a blank NEW product: every field of data() (name, code,
        // detail, category, price, flags, images, option groups, SKUs,
        // discount, open dialogs, the flash-column toggle), the hidden SKU
        // image input and the el-form validation marks. The WMS product list
        // is a lookup cache, not form data — it survives, so the next SKU
        // dialog does not have to fetch it again. Edit mode never resets.
        resetForm() {
            const fresh = this.$options.data.call(this);
            delete fresh.skuWmsProducts;
            delete fresh.skuWmsLoaded;
            delete fresh.skuWmsLoading;
            Object.assign(this.$data, fresh);
            const input = this.$refs && this.$refs.skuImageInput;
            if (input) input.value = "";
            this.$nextTick(() => {
                const form = this.$refs && this.$refs.productForm;
                if (form && typeof form.clearValidate === "function") form.clearValidate();
            });
        },
        // ---- #263: push to platform ----
        // Opening the modal does not call anything: the modal's own "ตรวจสอบ"
        // fires the dry run. Unsaved edits in this form are NOT part of the
        // push — the BE reads the saved product — so the operator has to save
        // first, which is why the modal shows the saved name.
        openPushModal() {
            this.pushModalOpen = true;
        },
        // ---- Option group UI ----
        _nextOptValId() {
            // Stable, unique negative temp id for an unsaved option value.
            this.tmpOptValSeq -= 1;
            return this.tmpOptValSeq;
        },
        addOptionGroup() {
            this.form.option_groups.push({
                id: 0,
                label_th: "",
                label_en: "",
                sort_order: this.form.option_groups.length,
                values: []
            });
        },
        removeOptionGroup(gIdx) {
            this.form.option_groups.splice(gIdx, 1);
        },
        startAddValue(gIdx) {
            this.addingValueFor = gIdx;
            this.newValueText = "";
            this.$nextTick(() => {
                const inputs = this.$refs.newValueInput;
                if (inputs) {
                    const el = Array.isArray(inputs) ? inputs[0] : inputs;
                    if (el && el.focus) el.focus();
                }
            });
        },
        confirmAddValue(gIdx) {
            const text = (this.newValueText || "").trim();
            if (text && gIdx === this.addingValueFor) {
                this.form.option_groups[gIdx].values.push({
                    id: this._nextOptValId(),
                    label_th: text,
                    label_en: "",
                    sort_order: this.form.option_groups[gIdx].values.length
                });
            }
            this.addingValueFor = null;
            this.newValueText = "";
        },
        removeOptionValue(gIdx, vIdx) {
            this.form.option_groups[gIdx].values.splice(vIdx, 1);
        },

        // ---- SKU Matrix generation ----
        // Slugify a label for use in a generated sku_code segment.
        _slugify(label) {
            return (label || "")
                .toLowerCase()
                .replace(/\s+/g, "-")
                .replace(/[^a-z0-9฀-๿-]/g, "")
                .replace(/-+/g, "-")
                .replace(/^-|-$/g, "");
        },
        // Cartesian product of arrays.
        _cartesian(arrays) {
            if (!arrays.length) return [[]];
            return arrays.reduce((acc, arr) => {
                const result = [];
                acc.forEach(a => {
                    arr.forEach(b => {
                        result.push([...a, b]);
                    });
                });
                return result;
            }, [[]]);
        },
        tryGenerateSkuMatrix() {
            // Validate: every group must have at least 1 value.
            for (const g of this.form.option_groups) {
                if (!g.values || g.values.length === 0) {
                    this.$notify({ type: "warning", message: this.$t("errors.product.option_group_empty") });
                    return;
                }
            }

            // Check if any existing matrix SKU rows would be dropped.
            const existingMatrix = this.form.skus.filter(s => s.option_value_ids && s.option_value_ids.length > 0);
            const newCombos = this._buildCombinations();
            const newKeySet = new Set(newCombos.map(c => this._comboKey(c)));

            const toDrop = existingMatrix.filter(s => {
                // Use the stable key stored at generation time (covers CREATE flow where
                // all ids are 0). Fall back to id-based key for rows loaded from BE.
                const key = s._comboSavedKey || this._comboKey(s.option_value_ids.map(id => ({ id })));
                return !newKeySet.has(key);
            });

            if (existingMatrix.length > 0 || this.form.skus.some(s => s.sku_code)) {
                this.matrixDropList = toDrop.filter(s => s.sku_code).map(s => s.sku_code);
                this.matrixConfirmVisible = true;
            } else {
                this.doGenerateSkuMatrix();
            }
        },
        _buildCombinations() {
            const groupValues = this.form.option_groups.map(g => g.values);
            return this._cartesian(groupValues);
        },
        _comboKey(combo) {
            // Stable, ORDER-INDEPENDENT key: one segment per value. Real ids (returned
            // from BE after first save) use "i<id>"; unsaved values (id===0) fall back to
            // the label text ("l:<label>") so two different unsaved values in the same
            // group stay distinct (sorting raw ids would collapse all id=0 values to one
            // key and corrupt re-generate stability). We sort the STRING segments so the
            // key does not depend on the order BE returns option_value_ids in — every
            // call site that compares combos must route through this same function.
            return combo
                .map(v => v.id !== 0 ? ("i" + v.id) : ("l:" + (v.label_th || v.label_en || "")))
                .sort()
                .join("|");
        },
        doGenerateSkuMatrix() {
            this.matrixConfirmVisible = false;
            const combos = this._buildCombinations();
            const productCode = (this.form.code || "PROD").toUpperCase();

            // Build a lookup of existing matrix SKUs by their stable combo key.
            // Use _comboSavedKey stored on the row (set at generation time) so the
            // key is consistent even when all option_value_ids are 0 (CREATE flow).
            const existingByKey = {};
            this.form.skus.forEach(s => {
                if (s.option_value_ids && s.option_value_ids.length > 0) {
                    const key = s._comboSavedKey || this._comboKey(s.option_value_ids.map(id => ({ id })));
                    existingByKey[key] = s;
                }
            });

            // Keep flat (hand-entered) SKUs that have no option_value_ids.
            const flatSkus = this.form.skus.filter(s => !s.option_value_ids || s.option_value_ids.length === 0);

            const newMatrixSkus = combos.map((combo, idx) => {
                const valueIds = combo.map(v => v.id);
                const key = this._comboKey(combo);
                const existing = existingByKey[key];

                // sku_name = value labels joined by " / "
                const sku_name = combo.map(v => v.label_th || v.label_en).join(" / ");

                // Generated sku_code: product.code + slugified value labels.
                const codeSuffix = combo.map(v => this._slugify(v.label_en || v.label_th)).filter(Boolean).join("-");
                // #257: sku_code is varchar(100) — a placeholder built from long option
                // labels must respect the same ceiling the WMS modal enforces.
                const generatedCode = (productCode + (codeSuffix ? "-" + codeSuffix.toUpperCase() : "-SKU" + (idx + 1)))
                    .slice(0, SKU_CODE_MAX_LENGTH);

                if (existing) {
                    // Preserve existing data and refresh the stable key. sku_name is
                    // free text since #257: keep a non-empty hand-typed name (owner
                    // ruling 2026-09-12); only a blank one gets the label-derived name.
                    const existingName = existing.sku_name == null ? "" : String(existing.sku_name).trim();
                    return {
                        ...existing,
                        sku_name: existingName !== "" ? existing.sku_name : sku_name,
                        option_value_ids: valueIds,
                        _comboSavedKey: key
                    };
                }
                return {
                    _idx: Date.now() + idx,
                    sku_code: generatedCode,
                    sku_name,
                    price: 0,
                    quantity: 0,
                    image_url: "",
                    image_key: "",
                    flash_price: undefined,
                    flashRange: null,
                    bonus_points: 0,
                    option_value_ids: valueIds,
                    _comboSavedKey: key
                };
            });

            this.form.skus = [...flatSkus, ...newMatrixSkus];
        },

        // ---- SKU code <- WMS product mapping (#246, target field #257) ----
        // GET /api/v3/wms/product serialises PascalCase (ID / Name / ManPN /
        // BarCode) — see ResProductListDetailV3 in the BO API. These two helpers
        // keep every read of a WMS row going through one place.
        wmsRowId(product) {
            if (!product) return null;
            const id = Number(product.ID !== undefined ? product.ID : product.id);
            return !id || isNaN(id) ? null : id;
        },
        // Primary label = the code that will actually be joined into sku_code
        // (man_pn — see wmsProductCode), product name second, so the user can
        // see what the generated code is being built from.
        wmsRowLabel(product) {
            if (!product) return "";
            const code = wmsProductCode(product);
            const name = product.Name || product.name || "";
            if (code && name && code !== name) return code + " - " + name;
            return code || name;
        },
        wmsProductById(id) {
            const wanted = Number(id);
            return this.skuWmsProducts.find(p => this.wmsRowId(p) === wanted);
        },
        // Fetch the WMS product list once per page visit and cache it. The
        // endpoint has no server-side search, so searching is done client-side.
        async ensureWmsProducts() {
            if (this.skuWmsLoaded || this.skuWmsLoading) return;
            this.skuWmsLoading = true;
            try {
                // mdf-bo-fn#246 follow-up: the light options endpoint, not
                // ProductListV3 — that one never returned on production data
                // and the dialog reported "load failed" while it was still running.
                const res = await WmsService.ProductOptions();
                const body = (res && res.data) || {};
                const datum = body.Datum || (Array.isArray(body.Data) ? body.Data[0] : body.Data) || {};
                const rows = datum.items || [];
                // Drop rows without a usable id: they can never be mapped and
                // would collide on the v-for key.
                this.skuWmsProducts = Array.isArray(rows)
                    ? rows.filter(r => this.wmsRowId(r) !== null)
                    : [];
                this.skuWmsLoaded = true;
            } catch (err) {
                this.skuWmsProducts = [];
                this.skuWmsLoaded = false;
                this.$notify({ type: "danger", message: this.$t("app_catalog.sku_wms_load_failed") });
            } finally {
                this.skuWmsLoading = false;
            }
        },
        openSkuWmsDialog(idx) {
            const row = this.form.skus[idx];
            if (!row) return;
            this.skuWmsRowIndex = idx;
            this.skuWmsPickId = null;
            this.skuWmsQuery = "";
            // Seed the draft from the SKU's existing mapping (tolerates array,
            // JSON string, '' and null — see normalizeWmsMap).
            this.skuWmsDraft = normalizeWmsMap(row.wms_map);
            this.skuWmsDialogVisible = true;
            this.ensureWmsProducts();
        },
        onSkuWmsQuery(query) {
            this.skuWmsQuery = query || "";
        },
        // Display helpers for a draft row. Resolved on every render (not cached
        // into the row) so a row added before the WMS list finished loading
        // starts showing its real code as soon as the list arrives.
        skuWmsRowCode(item) {
            return wmsProductCode(this.wmsProductById(item.wms_product_id)) || "#" + item.wms_product_id;
        },
        skuWmsRowName(item) {
            const product = this.wmsProductById(item.wms_product_id);
            return product ? (product.Name || product.name || "") : "";
        },
        addSkuWmsItem() {
            const id = Number(this.skuWmsPickId);
            if (!id) return;
            // wms_map is keyed by product — adding a product that is already in
            // the list means "one more of it" (owner ruling 2026-09-12): bump the
            // row's quantity instead of refusing, capped at skuWmsQtyMax.
            const existing = this.skuWmsDraft.find(item => item.wms_product_id === id);
            if (existing) {
                const current = wmsPickupQuantity(existing.pickup_quantity);
                if (current >= this.skuWmsQtyMax) {
                    this.$notify({ type: "warning", message: this.$t("app_catalog.sku_wms_qty_max", { max: this.skuWmsQtyMax }) });
                } else {
                    this.$set(existing, "pickup_quantity", current + 1);
                    this.$notify({ type: "info", message: this.$t("app_catalog.sku_wms_duplicate", { n: current + 1 }) });
                }
                this.skuWmsPickId = null;
                this.skuWmsQuery = "";
                return;
            }
            this.skuWmsDraft.push({ wms_product_id: id, pickup_quantity: 1 });
            this.skuWmsPickId = null;
            this.skuWmsQuery = "";
        },
        removeSkuWmsItem(idx) {
            this.skuWmsDraft.splice(idx, 1);
        },
        confirmSkuWmsDialog() {
            const row = this.form.skus[this.skuWmsRowIndex];
            if (!row) {
                this.skuWmsDialogVisible = false;
                return;
            }
            // Refuse to confirm while the catalogue is missing: every code would
            // resolve to '' and a perfectly good sku_code would be wiped.
            if (!this.skuWmsLoaded && this.skuWmsDraft.length > 0) {
                this.$notify({ type: "danger", message: this.$t("app_catalog.sku_wms_load_failed") });
                return;
            }
            // Same expansion as the live preview: each code repeated once per
            // unit of its quantity (#251), in list order.
            const generated = skuNameFromWmsMap(this.skuWmsDraft, item =>
                wmsProductCode(this.wmsProductById(item.wms_product_id))
            );
            // #257: mdfpf_product_sku.sku_code is varchar(100). Over that the BE
            // would truncate (silently breaking the stock key mdfpf_sku_stock.sku_code
            // and the auto-mapper round-trip) or reject the save, so refuse here —
            // BEFORE anything is written into the row — and leave the dialog open
            // so the operator can drop a unit.
            if (!skuCodeWithinLimit(generated, this.skuCodeMaxLength)) {
                this.$notify({
                    type: "danger",
                    message: this.$t("app_catalog.sku_code_too_long", { max: this.skuCodeMaxLength })
                });
                return;
            }
            // #257: the generated string is the CODE, not the name — the name is
            // free text the operator owns and this dialog must never touch it.
            this.$set(row, "sku_code", generated);
            // The saved mapping carries the quantity the user typed — a 2 must
            // survive (wmsPickupQuantity only repairs 0 / blank / junk to 1).
            this.$set(row, "wms_map", this.skuWmsDraft.map(item => ({
                wms_product_id: item.wms_product_id,
                pickup_quantity: wmsPickupQuantity(item.pickup_quantity)
            })));
            // Local-only marker (never sent): this row's mapping was set by hand,
            // so the save payload must transmit it even when it is now empty —
            // an empty array is how the BE is told to CLEAR the mapping.
            this.$set(row, "_wmsMapTouched", true);
            this.skuWmsDialogVisible = false;
        },

        // ---- #301 shared stock: who owns this SKU's quantity ----
        // Thin wrappers so the template stays declarative; the rules (including
        // "a missing stock_source is NOT wms") live in @/utils/stockSource.
        isWmsQuantity(row) {
            return isWmsSourced(row);
        },
        isManualQuantity(row) {
            return isManualSourced(row);
        },
        // "ตัวเลขนี้คำนวณจาก WMS (คงเหลือ X − รอตัด Y) — แก้ที่ WMS", or the
        // number-free version when the BE did not send the two diagnostics.
        wmsQuantityHint(row) {
            return wmsHint(row, this.$t.bind(this));
        },

        // ---- B7: the flash window Shopee owns ----
        isShopeeFlash(row) {
            return isShopeeFlashSku(row);
        },
        flashPriceText(row) {
            const price = row ? row.flash_price : null;
            if (price === null || price === undefined || price === "") return "-";
            const n = Number(price);
            return isFinite(n) ? n.toFixed(2) : "-";
        },
        // The window as Shopee set it, in Bangkok time. Read from `flashRange`
        // (what the loader built for the picker) so the cell shows the same two
        // instants the picker would have, not a second parse of the raw payload.
        flashWindowText(row) {
            const range = row && Array.isArray(row.flashRange) ? row.flashRange : [];
            const text = formatWindow(range[0], range[1]);
            return text === "" ? "-" : text;
        },

        // ---- SKU image ----
        // Return the CDN URL for the single SKU image (first image or image_url).
        skuImageUrl(row) {
            if (row.image_url) return row.image_url;
            if (Array.isArray(row.images) && row.images.length) {
                return row.images[0].cdn_url || row.images[0].url || "";
            }
            return "";
        },
        // Trigger file picker for a specific SKU index.
        triggerSkuImage(idx) {
            this.skuImageTarget = idx;
            this.$refs.skuImageInput.value = "";
            this.$refs.skuImageInput.click();
        },
        // Handle file selection — presign -> PUT -> set SKU image_url + image_key.
        async onSkuImageChange(e) {
            const file = e.target.files && e.target.files[0];
            e.target.value = "";
            if (!file) return;
            const idx = this.skuImageTarget;
            if (idx === null || idx === undefined) return;
            const sku = this.form.skus[idx];
            if (!sku) return;
            const maxBytes = 5 * 1024 * 1024;
            if (file.size > maxBytes) {
                this.$notify({ type: "danger", message: this.$t("errors.upload.file_too_large") || "File too large (max 5MB)" });
                return;
            }
            try {
                const presignRes = await AppCatalogUploadService.presign({
                    object_type: "sku",
                    file_name: file.name,
                    content_type: file.type
                });
                const { upload_url, cdn_url, object_key } = presignRes.data;
                await new Promise((resolve, reject) => {
                    const xhr = new XMLHttpRequest();
                    xhr.open("PUT", upload_url);
                    xhr.setRequestHeader("Content-Type", file.type);
                    xhr.onload = () => {
                        if (xhr.status >= 200 && xhr.status < 300) resolve();
                        else reject(new Error("Upload failed: " + xhr.status));
                    };
                    xhr.onerror = () => reject(new Error("Network error"));
                    xhr.send(file);
                });
                this.$set(this.form.skus[idx], "image_url", cdn_url);
                this.$set(this.form.skus[idx], "image_key", object_key || "");
            } catch (err) {
                const detail = (err && err.message) ? ": " + err.message : "";
                this.$notify({ type: "danger", message: (this.$t("errors.upload.presign_failed") || "Upload failed") + detail });
            }
        },
        // Rich-text detail image upload: presign -> PUT to S3 -> insert CDN URL.
        async handleDetailImageAdded(file, Editor, cursorLocation, resetUploader) {
            try {
                const presignRes = await AppCatalogUploadService.presign({
                    object_type: "product",
                    file_name: file.name,
                    content_type: file.type
                });
                const { upload_url, cdn_url } = presignRes.data;
                await new Promise((resolve, reject) => {
                    const xhr = new XMLHttpRequest();
                    xhr.open("PUT", upload_url);
                    xhr.setRequestHeader("Content-Type", file.type);
                    xhr.onload = () => {
                        if (xhr.status >= 200 && xhr.status < 300) resolve();
                        else reject(new Error("Upload failed: " + xhr.status));
                    };
                    xhr.onerror = () => reject(new Error("Network error"));
                    xhr.send(file);
                });
                Editor.insertEmbed(cursorLocation, "image", cdn_url);
                resetUploader();
            } catch (err) {
                const detail = (err && err.message) ? ": " + err.message : "";
                this.$notify({ type: "danger", message: (this.$t("errors.upload.presign_failed") || "Upload failed") + detail });
                resetUploader();
            }
        },
        ...mapActions({
            fetchOne: "AppCatalogProduct/fetchOne",
            createItem: "AppCatalogProduct/create",
            updateItem: "AppCatalogProduct/update"
        }),
        async loadProduct() {
            this.loadingProduct = true;
            try {
                await this.fetchOne(this.$route.params.id);
                const p = this.$store.getters["AppCatalogProduct/current"];
                if (p) {
                    this.form = {
                        name_th: p.name_th || "",
                        name_en: p.name_en || "",
                        name: p.name || "",
                        code: p.code || "",
                        detail: p.detail || "",
                        bo_product_category_id: p.bo_product_category_id || null,
                        price: p.price || 0,
                        price_mode: p.price_mode || "per_sku",
                        cod: p.cod || 0,
                        app_enable: p.app_enable !== undefined ? p.app_enable : 1,
                        app_tag: p.app_tag || "",
                        visibility: p.visibility !== undefined ? p.visibility : 1,
                        images: Array.isArray(p.images2) ? p.images2 : [],
                        option_groups: Array.isArray(p.option_groups) ? p.option_groups.map(g => ({
                            id: g.id || 0,
                            label_th: g.label_th || "",
                            label_en: g.label_en || "",
                            sort_order: g.sort_order || 0,
                            values: Array.isArray(g.values) ? g.values.map(v => ({
                                id: v.id || 0,
                                label_th: v.label_th || "",
                                label_en: v.label_en || "",
                                sort_order: v.sort_order || 0
                            })) : []
                        })) : [],
                        // #301: the spread carries `stock_source`, `wms_available`
                        // and `pending_reserved` through untouched — they are the
                        // only thing the quantity cell reads to decide whether the
                        // warehouse owns the number. Mapping the SKU field by field
                        // here (instead of spreading) would drop them silently and
                        // hand the input back to staff on a pushed SKU.
                        skus: (p.skus || []).map((s, i) => ({
                            ...s,
                            _idx: i,
                            // Single image: keep image_url from BE or derive from images[0].
                            image_url: s.image_url || (Array.isArray(s.images) && s.images[0] ? (s.images[0].cdn_url || s.images[0].url || "") : ""),
                            image_key: s.image_key || (Array.isArray(s.images) && s.images[0] ? (s.images[0].object_key || "") : ""),
                            // Flash fields (Part C).
                            flash_price: (s.flash_price === null || s.flash_price === undefined) ? undefined : s.flash_price,
                            flashRange: (s.flash_starts_at && s.flash_ends_at)
                                ? [this.toPickerLocal(s.flash_starts_at), this.toPickerLocal(s.flash_ends_at)]
                                : null,
                            bonus_points: s.bonus_points || 0,
                            option_value_ids: Array.isArray(s.option_value_ids) ? s.option_value_ids : [],
                            // #246: the WMS mapping that generated sku_code. BE may
                            // send it as an array or as raw JSON text.
                            wms_map: normalizeWmsMap(s.wms_map)
                        })),
                        app_discount: p.app_discount || 0,
                        app_discount_start: this.toPickerLocal(p.app_discount_start),
                        app_discount_end: this.toPickerLocal(p.app_discount_end)
                    };
                    // #257: the flash columns open only for a product that already
                    // uses them. Evaluated AFTER the form is built so it reads the
                    // mapped rows (flashRange), not the raw BE payload.
                    this.showFlashColumns = anySkuHasFlashSale(this.form.skus);
                }
            } finally {
                this.loadingProduct = false;
            }
        },
        addSku() {
            // #257: the table no longer lets staff type a code, so a flat SKU gets the
            // same placeholder shape the matrix uses until the WMS modal sets it.
            const productCode = (this.form.code || "PROD").toUpperCase();
            const placeholder = (productCode + "-SKU" + (this.form.skus.length + 1)).slice(0, SKU_CODE_MAX_LENGTH);
            this.form.skus.push({
                _idx: Date.now(),
                sku_code: placeholder,
                sku_name: "",
                price: 0,
                quantity: 0,
                image_url: "",
                image_key: "",
                flash_price: undefined,
                flashRange: null,
                bonus_points: 0,
                option_value_ids: [],
                // #246 + #257: sku_code is generated from this list via the edit dialog.
                wms_map: []
            });
        },
        removeSku(idx) {
            this.form.skus.splice(idx, 1);
        },
        toPickerLocal(dateStr) {
            // Convert an RFC3339 instant from BE into the picker's local
            // wall-clock format ("yyyy-MM-dd HH:mm:ss"). Null/invalid -> null.
            if (!dateStr) return null;
            const d = new Date(dateStr);
            if (isNaN(d.getTime())) return null;
            const pad = n => String(n).padStart(2, "0");
            return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
        },
        toIsoInstant(localStr) {
            // Convert the picker's local wall-clock ("yyyy-MM-dd HH:mm:ss") into a
            // true RFC3339 instant. Empty/invalid -> null so BE omits the field.
            if (!localStr) return null;
            const d = new Date(localStr.replace(" ", "T"));
            if (isNaN(d.getTime())) return null;
            return d.toISOString();
        },
        submitForm() {
            this.$refs.productForm.validate(async (valid) => {
                if (!valid) return;

                // Client-side validation for price_mode=fixed.
                if (this.form.price_mode === "fixed" && (!this.form.price || this.form.price <= 0)) {
                    this.$notify({ type: "danger", message: this.$t("errors.product.fixed_price_required") });
                    return;
                }

                // #257: every code on the page must fit varchar(100) — the modal guards
                // its own writes, this covers placeholders and pre-existing data.
                if (this.form.skus.some(s => !skuCodeWithinLimit(s.sku_code, this.skuCodeMaxLength))) {
                    this.$notify({ type: "danger", message: this.$t("app_catalog.sku_code_too_long", { max: this.skuCodeMaxLength }) });
                    return;
                }
                // Client-side SKU code uniqueness check.
                const codes = this.form.skus.map(s => s.sku_code).filter(Boolean);
                const uniqueCodes = new Set(codes);
                if (codes.length !== uniqueCodes.size) {
                    this.$notify({ type: "danger", message: this.$t("errors.sku.duplicate_code") });
                    return;
                }
                // Owner rule 2026-10-15: "SKU ที่ไม่มีชื่อตัวเลือก ถือว่าไม่สมบูรณ์ ห้ามเปิดขาย".
                // Putting the product on sale with a live blank-name SKU is refused
                // here, before the request — the BE answers the same 400 anyway.
                // With the switch off the product saves as a draft. #976: only when
                // more than one SKU is on — a single-SKU product needs no name.
                if (optionNameSaveGuard(this.form).blocked) {
                    this.skuNameChecked = true;
                    this.$notify({ type: "danger", message: this.$t(SKU_NAME_REQUIRED_KEY) });
                    return;
                }

                this.saving = true;
                try {
                    const imageList = Array.isArray(this.form.images) ? this.form.images : [];
                    const payload = {
                        name_th: this.form.name_th,
                        name_en: this.form.name_en,
                        name: this.form.name_th,
                        code: this.form.code,
                        detail: this.form.detail,
                        bo_product_category_id: this.form.bo_product_category_id,
                        price: this.form.price,
                        price_mode: this.form.price_mode,
                        cod: this.form.cod,
                        app_enable: this.form.app_enable,
                        app_tag: this.form.app_tag,
                        visibility: this.form.visibility,
                        image_keys: imageList
                            .filter(img => img && typeof img === "object" && img.object_key)
                            .map(img => img.object_key),
                        option_groups: this.form.option_groups.map((g, gi) => ({
                            id: g.id || 0,
                            label_th: g.label_th,
                            label_en: g.label_en,
                            sort_order: gi,
                            values: (g.values || []).map((v, vi) => ({
                                id: v.id || 0,
                                label_th: v.label_th,
                                label_en: v.label_en,
                                sort_order: vi
                            }))
                        })),
                        // #301: stripWmsQuantities removes the `quantity` KEY from
                        // every WMS-sourced SKU (see @/utils/stockSource). Sending
                        // the value we loaded would be worse than sending nothing:
                        // the mirror is refreshed every 20 s, so by the time save is
                        // pressed it can roll the shop back to stock that has sold.
                        //
                        // B7 §7.7: applyFlashSource then stamps `flash_source` —
                        // 'manual' on a SKU whose flash a human is setting, 'shopee'
                        // echoed back on one the mirror owns. WITHOUT the 'manual'
                        // claim the next ticker round (10 min) overwrites the number
                        // staff just typed, silently, because §4.6 only spares a
                        // window whose source is NULL/'manual'.
                        skus: applyFlashSource(stripWmsQuantities(this.form.skus.map(s => {
                            const sku = {
                                sku_code: s.sku_code,
                                sku_name: s.sku_name,
                                price: s.price,
                                quantity: s.quantity,
                                // Single image key (Part A); BE accepts image_key per SKU.
                                image_key: s.image_key || "",
                                // Flash fields (Part C).
                                flash_price: (s.flash_price === null || s.flash_price === undefined || s.flash_price === "")
                                    ? null
                                    : Number(s.flash_price),
                                flash_starts_at: (s.flashRange && s.flashRange[0])
                                    ? new Date(s.flashRange[0]).toISOString()
                                    : null,
                                flash_ends_at: (s.flashRange && s.flashRange[1])
                                    ? new Date(s.flashRange[1]).toISOString()
                                    : null,
                                bonus_points: Number(s.bonus_points) || 0,
                                option_value_ids: Array.isArray(s.option_value_ids) ? s.option_value_ids : []
                            };
                            // #246 + #257: the WMS products the SKU code was generated from
                            // ([{ wms_product_id, pickup_quantity }], order significant).
                            // Without this key the mapping never round-trips and the
                            // generated code cannot be reproduced after a reload.
                            //
                            // The BE contract for PUT /api/v1/bo/catalog/products/:id
                            // is tri-state per SKU: items = replace, [] = CLEAR, key
                            // omitted = keep what is stored. So only send it when this
                            // row's mapping is actually known — the user edited it
                            // through the dialog (including clearing it), or the GET
                            // returned one. Blindly sending [] for every row would wipe
                            // stored mappings the moment this FE runs against a BE
                            // whose GET does not return wms_map yet.
                            if (s._wmsMapTouched || (Array.isArray(s.wms_map) && s.wms_map.length > 0)) {
                                sku.wms_map = s.wms_map || [];
                            }
                            return sku;
                        }), this.form.skus), this.form.skus),
                        app_discount: this.form.app_discount,
                        app_discount_start: this.toIsoInstant(this.form.app_discount_start),
                        app_discount_end: this.toIsoInstant(this.form.app_discount_end)
                    };

                    let res = null;
                    const wasNew = this.isNew;
                    if (wasNew) {
                        res = await this.createItem(payload);
                    } else {
                        res = await this.updateItem({ id: this.$route.params.id, payload });
                    }

                    // #301: the BE drops `quantity` for a SKU it owns even when a
                    // client sends one (§8, "ป้องกัน client เก่า") and reports it
                    // back as `quantity_ignored`. We strip it up front, so this
                    // firing means the two sides disagree about which SKUs are
                    // pushed — the operator must know their number did not land
                    // rather than find out from the shop later.
                    const ignored = quantityIgnoredSkus(res);
                    if (ignored.length > 0) {
                        this.$notify({
                            type: "info",
                            message: this.$t("app_catalog.sku_quantity_ignored", {
                                n: ignored.length,
                                codes: ignored.filter(Boolean).join(", ")
                            })
                        });
                    }

                    this.$notify({ type: "success", message: this.$t("app_catalog.save_success") });
                    // Form draft §4 A8: the cached /new tab must be empty the
                    // next time it is opened — reset BEFORE leaving. Edit mode
                    // keeps what was just saved.
                    if (wasNew) this.afterSaveReset();
                    requestRefresh("/seller/products");
                    this.$router.push("/seller/products");
                } catch (err) {
                    // The BE's 400 sku_name_required (a SKU the client guard could
                    // not see, e.g. an older tab): same sentence, rows marked.
                    if (isOptionNameRequiredError(err)) {
                        this.skuNameChecked = true;
                        this.$notify({ type: "danger", message: this.$t(SKU_NAME_REQUIRED_KEY) });
                        return;
                    }
                    const msg = apiErrorMessage(err, this.$t);
                    this.$notify({ type: "danger", message: msg });
                } finally {
                    this.saving = false;
                }
            });
        }
    }
};
</script>

<style scoped>
/* #246 + #257: the SKU code is a generated, read-only value — long "A+B+C" chains
   must wrap inside the table cell instead of widening the column. */
.sku-code-readonly {
    word-break: break-word;
}

/* Owner rule 2026-10-15: the SKU-name input of a row refused for having no
   option name. Outside an el-form-item, so element-ui's own .is-error border
   does not apply. */
.sku-name-missing >>> .el-input__inner {
    border-color: #f5365c;
}

/* #301: the quantity a WMS-sourced SKU mirrors. Rendered as a number, not as a
   disabled input — a greyed-out input box invites clicking and typing, and the
   point is that there is nothing to type here. */
.wms-quantity-readonly {
    font-weight: 600;
}

/* B7: the flash price/window a Shopee promotion owns. Same treatment as the
   WMS quantity above and for the same reason — a disabled input invites typing,
   and there is nothing to type here: the mirror rewrites it every 10 minutes. */
.shopee-flash-readonly {
    font-weight: 600;
    word-break: break-word;
}
</style>
